import React from "react";
import PropTypes from "prop-types";
import { icons } from "lucide-react";
import { Box, Flex, Button as ThemeUIBtn, Text } from "theme-ui";
import { colors } from "../../styles/theme";
import { typography } from "../../styles/theme";

const IconContainer = ({ iconName, direction }) => {
  const LucideIcon = icons[iconName];
  const iconStyle =
    direction === "rtl" ? { marginLeft: "0.5rem" } : { marginRight: "0.5rem" };

  return <LucideIcon size={24} style={iconStyle} />;
};

const Button = ({
  iconName,
  direction,
  children,
  size,
  disabled,
  variant,
  btnWidth,
  onClick,
  id,
  ref,
  customStyles,
}) => {
  const renderIcon = (
    <IconContainer iconName={iconName} direction={direction} />
  );
  return (
    <ThemeUIBtn
      ref={ref}
      sx={{
        ...styles[size],
        ...styles.btn,
        ...styles[variant],
        width: btnWidth === "full" ? "100%" : "auto",
        ...customStyles,
      }}
      onClick={onClick}
      disabled={disabled}
      id={id}
    >
      <Flex sx={styles.btnContent}>
        {iconName && direction === "ltr" && renderIcon}
        <Text>{children}</Text>
        {iconName && direction === "rtl" && renderIcon}
      </Flex>
    </ThemeUIBtn>
  );
};

const styles = {
  large: {
    py: "0.875rem",
    px: "2rem",
  },
  medium: {
    py: "0.625rem",
    px: "1.563rem",
  },
  small: {
    py: "0.375rem",
    px: "0.938rem",
  },
  btn: {
    borderRadius: "100px",
    alignItems: "center",
    ...typography.textMd500,
  },
  btnContent: { justifyContent: "center", alignItems: "center" },
  filled: {
    backgroundColor: colors.primary1000,
    color: colors.white,
    border: `2px solid ${colors.primary1000}`,
    minHeight: "3.375rem",
    "&:hover": {
      backgroundColor: colors.primaryMain,
      border: `2px solid ${colors.primary1000}`
    },
    "&:active": {
      backgroundColor: colors.black,
    },
    "&:focus": {
      backgroundColor: colors.primaryMain,
      border: `2px solid ${colors.primary200}`,
    },
    "&:disabled": {
      backgroundColor: colors.neutral300,
      cursor: 'not-allowed',
      border: `2px solid ${colors.neutral300}`
    },
  },
  outlined: {
    color: colors.primary1000,
    backgroundColor: colors.white,
    border: `2px solid ${colors.primary1000}`,
    minHeight: "3.375rem",
    "&:hover": {
      color: colors.black,
      backgroundColor: colors.primary200,
      border: `2px solid ${colors.primary100}`,
    },
    "&:active": {
      color: colors.black,
      backgroundColor: colors.neutral300,
    },
    "&:focus": {
      color: colors.black,
      backgroundColor: colors.primary300,
      border: `2px solid ${colors.primary200}`,
    },
    "&:disabled": {
      color: colors.neutral600,
      border: `2px solid ${colors.neutral200}`,
      cursor: 'not-allowed',
      "&:hover": {
        color: colors.neutral600,
        border: `2px solid ${colors.neutral200}`,
        backgroundColor: colors.white,
      }
    },
  },
};

Button.propTypes = {
  iconName: PropTypes.string,
  direction: PropTypes.oneOf(["rtl", "ltr"]),
  children: PropTypes.string.isRequired,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf(["filled", "outlined"]),
  onClick: PropTypes.func.isRequired,
  id: PropTypes.string,
  btnWidth: PropTypes.oneOf(["default", "full"]),
  customStyles: PropTypes.object,
};

Button.defaultProps = {
  direction: "rtl",
  btnWidth: "default",
  children: "button",
  size: "medium",
  variant: "outlined",
  disabled: false,
  onClick: () => {},
  id: "btn",
  customStyles: {},
  ref: null
};

export default Button;
